import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Eye,
  Search,
  TrendingUp,
  ShoppingCart,
  Heart,
  ArrowRight,
  Loader2,
  AlertCircle,
} from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { AnalyticsFreshnessBanner } from "@/components/admin/shared/AnalyticsFreshnessBanner";
import { analyticsApi } from "@/lib/admin-api";

// ---------------------------------------------------------------------------
// Period options
// ---------------------------------------------------------------------------

const PERIOD_OPTIONS = [
  { label: "7 jours", value: 7 },
  { label: "30 jours", value: 30 },
  { label: "90 jours", value: 90 },
] as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRate(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function rateBadgeColor(rate: number): string {
  if (rate >= 0.1) return "bg-emerald-500/20 text-emerald-400";
  if (rate >= 0.03) return "bg-amber-500/20 text-amber-400";
  return "bg-red-500/20 text-red-400";
}

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------

function Section({
  title,
  icon: Icon,
  children,
}: {
  readonly title: string;
  readonly icon: React.ElementType;
  readonly children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <div className="flex items-center gap-2 mb-5">
        <Icon size={18} className="text-secondary" />
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1. Search funnel bar chart
// ---------------------------------------------------------------------------

function SearchFunnelChart({
  funnel,
}: {
  readonly funnel: {
    readonly searches: number;
    readonly clicks: number;
    readonly cart_adds: number;
    readonly purchases: number;
    readonly search_to_click_rate: number;
    readonly click_to_cart_rate: number;
    readonly cart_to_purchase_rate: number;
  };
}) {
  const stages = [
    { label: "Recherches", value: funnel.searches, color: "bg-blue-500" },
    { label: "Clics", value: funnel.clicks, color: "bg-cyan-500", rate: funnel.search_to_click_rate },
    { label: "Ajouts panier", value: funnel.cart_adds, color: "bg-amber-500", rate: funnel.click_to_cart_rate },
    { label: "Achats", value: funnel.purchases, color: "bg-emerald-500", rate: funnel.cart_to_purchase_rate },
  ];

  const maxValue = Math.max(...stages.map((s) => s.value), 1);

  return (
    <div className="space-y-4">
      {stages.map((stage, idx) => (
        <div key={stage.label}>
          <div className="flex items-center justify-between text-sm mb-1.5">
            <div className="flex items-center gap-2">
              <span className="font-medium text-foreground">{stage.label}</span>
              {stage.rate != null && (
                <span className="text-xs text-muted-foreground tabular-nums">
                  ({formatRate(stage.rate)})
                </span>
              )}
            </div>
            <span className="tabular-nums text-muted-foreground">
              {stage.value.toLocaleString("fr-FR")}
            </span>
          </div>
          <div className="h-8 w-full rounded-lg bg-muted/30 overflow-hidden">
            <div
              className={`h-full rounded-lg ${stage.color} transition-all duration-500`}
              style={{ width: `${Math.max((stage.value / maxValue) * 100, 2)}%` }}
            />
          </div>
          {/* Arrow between stages */}
          {idx < stages.length - 1 && (
            <div className="flex justify-center py-1 text-muted-foreground/40">
              <ArrowRight size={14} className="rotate-90" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Intent() {
  const [days, setDays] = useState<number>(7);

  const {
    data: intentData,
    isLoading: intentLoading,
    isError: intentError,
  } = useQuery({
    queryKey: ["analytics", "intent", days],
    queryFn: () => analyticsApi.intent(days),
  });

  const {
    data: funnelData,
    isLoading: funnelLoading,
    isError: funnelError,
  } = useQuery({
    queryKey: ["analytics", "search-funnel", days],
    queryFn: () => analyticsApi.searchFunnel(days),
  });

  const isLoading = intentLoading || funnelLoading;
  const isError = intentError || funnelError;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <AnalyticsFreshnessBanner />
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold text-foreground">
              Que cherchent nos clients ?
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Intentions d'achat et comportements de recherche
            </p>
          </div>

          {/* Period selector */}
          <div className="flex items-center gap-1 rounded-lg border border-border bg-card p-1">
            {PERIOD_OPTIONS.map((opt) => (
              <button
                type="button"
                key={opt.value}
                onClick={() => setDays(opt.value)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  days === opt.value
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-muted-foreground" size={28} />
          </div>
        )}

        {/* Error */}
        {isError && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-6 text-center">
            <AlertCircle className="mx-auto mb-2 text-destructive" size={24} />
            <p className="text-sm text-destructive">
              Impossible de charger les données d'intention.
            </p>
          </div>
        )}

        {!isLoading && !isError && (
          <>
            {/* 1. Entonnoir de recherche */}
            {funnelData && (
              <Section title="Entonnoir de recherche" icon={Search}>
                <SearchFunnelChart funnel={funnelData.data.funnel} />
                {funnelData.data.zero_result_rate > 0 && (
                  <p className="mt-4 text-xs text-muted-foreground">
                    Taux de recherches sans résultat :{" "}
                    <span className="text-red-400 font-medium">
                      {formatRate(funnelData.data.zero_result_rate)}
                    </span>
                  </p>
                )}
              </Section>
            )}

            {/* 2. Recherches sans r\u00e9sultats */}
            {intentData && intentData.data.search_gaps.length > 0 && (
              <Section title="Recherches sans résultats" icon={AlertCircle}>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                          Requête
                        </th>
                        <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">
                          Nombre
                        </th>
                        <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">
                          Dernière recherche
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {intentData.data.search_gaps.map((gap) => (
                        <tr
                          key={gap.query}
                          className="border-b border-border last:border-0 bg-red-500/5 hover:bg-red-500/10 transition-colors"
                        >
                          <td className="px-4 py-2.5 font-medium text-foreground">
                            {gap.query}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">
                            {gap.search_count.toLocaleString("fr-FR")}
                          </td>
                          <td className="px-4 py-2.5 text-right text-muted-foreground">
                            {formatDate(gap.last_searched_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>
            )}

            {/* 3. Recherches tendance */}
            {intentData && intentData.data.trending_searches.length > 0 && (
              <Section title="Recherches tendance" icon={TrendingUp}>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                          Requête
                        </th>
                        <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">
                          Total
                        </th>
                        <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">
                          Taux de clic
                        </th>
                        <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">
                          Conversion
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {intentData.data.trending_searches.map((ts) => (
                        <tr
                          key={ts.query}
                          className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
                        >
                          <td className="px-4 py-2.5 font-medium text-foreground">
                            {ts.query}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">
                            {ts.total_searches.toLocaleString("fr-FR")}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium tabular-nums ${rateBadgeColor(ts.click_through_rate)}`}
                            >
                              {formatRate(ts.click_through_rate)}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium tabular-nums ${rateBadgeColor(ts.conversion_rate)}`}
                            >
                              {formatRate(ts.conversion_rate)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>
            )}

            {/* 4. Produits \u00e0 forte intention */}
            {intentData && intentData.data.high_intent_products.length > 0 && (
              <Section title="Produits à forte intention" icon={ShoppingCart}>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {intentData.data.high_intent_products.map((product) => (
                    <div
                      key={product.product_id}
                      className="rounded-lg border border-border bg-muted/10 p-4 space-y-3"
                    >
                      <p className="font-medium text-foreground text-sm leading-snug">
                        {product.product_name}
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <p className="text-xs text-muted-foreground">
                            Score d'intention
                          </p>
                          <p className="text-lg font-bold text-foreground tabular-nums">
                            {product.intent_score.toFixed(1)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">
                            Ajouts panier
                          </p>
                          <p className="text-sm font-semibold text-foreground tabular-nums">
                            {product.cart_adds.toLocaleString("fr-FR")}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Achats</p>
                          <p className="text-sm font-semibold text-emerald-400 tabular-nums">
                            {product.purchases.toLocaleString("fr-FR")}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* 5. Les plus ajout\u00e9s en favoris */}
            {intentData && intentData.data.top_wishlisted.length > 0 && (
              <Section title="Les plus ajoutés en favoris" icon={Heart}>
                <div className="space-y-2">
                  {intentData.data.top_wishlisted.map((item, idx) => (
                    <div
                      key={item.product_id}
                      className="flex items-center gap-3 rounded-lg border border-border bg-muted/10 px-4 py-3"
                    >
                      <span className="text-sm font-medium text-muted-foreground w-6 text-center tabular-nums">
                        {idx + 1}
                      </span>
                      <Heart
                        size={16}
                        className="text-red-400 fill-red-400 shrink-0"
                      />
                      <span className="flex-1 text-sm font-medium text-foreground truncate" title={item.product_id}>
                        {item.product_name ?? `Produit ${item.product_id.slice(0, 8)}…`}
                      </span>
                      <span className="text-sm font-semibold text-foreground tabular-nums">
                        {item.wishlist_count.toLocaleString("fr-FR")}
                      </span>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Empty state when no intent data at all */}
            {intentData &&
              intentData.data.search_gaps.length === 0 &&
              intentData.data.trending_searches.length === 0 &&
              intentData.data.high_intent_products.length === 0 &&
              intentData.data.top_wishlisted.length === 0 &&
              !funnelData && (
                <div className="rounded-xl border border-border bg-card p-8 text-center">
                  <Eye
                    size={32}
                    className="mx-auto mb-3 text-muted-foreground"
                  />
                  <p className="text-muted-foreground">
                    Aucune donnée d'intention disponible pour cette période.
                  </p>
                </div>
              )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
